<template>
  <Flex direction="column" align="stretch" :gap="2">
    <Button
      :label="t('sign_in.action')"
      :disabled="!!remainingTime || OTPCodeSendLoading"
      @click="sendOtpCode" />
    <span
      class="resend-code md-typescale-body-large"
      :class="!remainingTime ? 'hidden' : ''">
      {{ t('sign_in.resend_code') }} {{ toMMSS(remainingTime) }}
    </span>
  </Flex>
</template>

<script lang="ts" setup>
import Button from '@/components/Button/Button.vue';
import { useI18n } from 'vue-i18n';
import Flex from '@/components/Flex/Flex.vue';
import { useSendOtpCode } from './composables/useSendOtpCode';
import { onBeforeMount, onUnmounted, ref } from 'vue';
import { SMS_RESEND_TIME } from '@/constants';
import { toMMSS } from './utils/toMMSS';
import { useToast } from '@/composables/useToast';
import { useCountdownStorage } from '@/features/SmsCodeVerification/components/SendOtpForm/composables/useCountdownStorage';

const { t } = useI18n();
const { showToast } = useToast();

const emit = defineEmits<{
  (event: 'success'): void;
  (event: 'expired'): void;
}>();

const props = defineProps<{
  usersPhone: string;
}>();

const { getRemainingTime, setNewTime } = useCountdownStorage(props.usersPhone);

const { mutateAsync: sendOTPCodeMutation, isPending: OTPCodeSendLoading } =
  useSendOtpCode(props.usersPhone);

const remainingTime = ref(0);
let timer = -1;

onBeforeMount(() => {
  if (!getRemainingTime()) {
    return;
  }

  startTimer(getRemainingTime());
  emit('success');
});

const startTimer = (seconds: number) => {
  remainingTime.value = seconds;

  if (!seconds) {
    return;
  }

  timer = setInterval(() => {
    remainingTime.value -= 1;

    if (remainingTime.value <= 0) {
      emit('expired');
      clearInterval(timer);
    }
  }, 1000);
};

onUnmounted(() => {
  clearInterval(timer);
});

const sendOtpCode = async () => {
  await sendOTPCodeMutation()
    .then(() => {
      startTimer(SMS_RESEND_TIME);
      setNewTime();

      emit('success');
    })
    .catch(() => {
      showToast(t('general.response_errors.GENERIC_ERROR_title'), {
        type: 'error',
      });
    });
};
</script>
<style>
.resend-code {
  color: var(--md-resend-color);
  text-align: center;

  &.hidden {
    visibility: hidden;
  }
}
</style>
